import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import "@/App.css";
import { Button } from "./components/ui/button";
import { Card } from "./components/ui/card";
import { Progress } from "./components/ui/progress";
import { Badge } from "./components/ui/badge";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./components/ui/dialog";
import { Terminal, Shield, Lock, Cpu, Mail, Send, MessageSquare, Twitter } from "lucide-react";
import { toast } from "sonner";
import MatrixRain from "./components/MatrixRain";
import KaliTerminal from "./components/KaliTerminal";
import ASCIIBanner from "./components/ASCIIBanner";
import SystemStatus from "./components/SystemStatus";

function App() {
  const [accessLevel, setAccessLevel] = useState(3301);
  const [selectedProject, setSelectedProject] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [bootComplete, setBootComplete] = useState(false);

  useEffect(() => {
    // Boot sequence simulation
    const timer = setTimeout(() => setBootComplete(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const skills = [
    { name: "Digital Forensics (FOR508)", level: 98, color: "--neon-green", icon: <Terminal className="w-6 h-6" />, desc: "Advanced Incident Response & Threat Hunting" },
    { name: "Incident Handling (SEC504)", level: 95, color: "--neon-cyan", icon: <Shield className="w-6 h-6" />, desc: "Hacker Tools, Techniques & Exploits" },
    { name: "Memory Forensics", level: 92, color: "--neon-purple", icon: <Cpu className="w-6 h-6" />, desc: "Malware Analysis & Reverse Engineering" },
    { name: "Penetration Testing", level: 96, color: "--neon-red", icon: <Lock className="w-6 h-6" />, desc: "Network Security & Vulnerability Assessment" },
    { name: "Timeline Analysis", level: 90, color: "--neon-green", icon: <Terminal className="w-6 h-6" />, desc: "Event Correlation & Investigation" },
    { name: "Exploit Development", level: 88, color: "--neon-cyan", icon: <Shield className="w-6 h-6" />, desc: "Zero-Day Research & Weaponization" }
  ];

  const projects = [
    {
      id: 1,
      title: "[CLASSIFIED]",
      description: "Top Secret - Level 5 Clearance Required",
      tags: ["Exploit", "Zero-Day", "Classified"],
      accessLevel: 5,
      details: "This project contains sensitive information regarding advanced penetration testing methodologies and exploit development. Access restricted to authorized personnel only.",
    },
    {
      id: 2,
      title: "[REDACTED]",
      description: "Top Secret - Level 6 Clearance Required",
      tags: ["AI", "Neural Net", "Classified"],
      accessLevel: 6,
      details: "Advanced AI system for threat detection and autonomous response. Details are classified under national security protocols.",
    },
    {
      id: 3,
      title: "[ENCRYPTED]",
      description: "Top Secret - Level 7 Clearance Required",
      tags: ["Crypto", "Blockchain", "Classified"],
      accessLevel: 7,
      details: "Quantum-resistant cryptographic implementation for secure communications. All technical specifications are encrypted.",
    },
    {
      id: 4,
      title: "[CONFIDENTIAL]",
      description: "Top Secret - Level 4 Clearance Required",
      tags: ["OSINT", "Recon", "Classified"],
      accessLevel: 4,
      details: "Open-source intelligence gathering framework with advanced data correlation capabilities.",
    },
    {
      id: 5,
      title: "[RESTRICTED]",
      description: "Top Secret - Level 5 Clearance Required",
      tags: ["Malware", "Analysis", "Classified"],
      accessLevel: 5,
      details: "Automated malware analysis and reverse engineering platform. Access logs are monitored.",
    },
    {
      id: 6,
      title: "[TOP SECRET]",
      description: "Top Secret - Level 8 Clearance Required",
      tags: ["APT", "Defense", "Classified"],
      accessLevel: 8,
      details: "Advanced Persistent Threat detection and mitigation system. Highest level clearance required.",
    }
  ];

  const handleAccessGain = () => {
    setAccessLevel(prev => prev + 1);
    toast.success("Access Level Increased!", {
      duration: 2000,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success("Message encrypted and sent! 🔐", {
      duration: 3000,
    });
    setFormData({ name: '', email: '', message: '' });
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] font-terminal text-[hsl(var(--foreground))] relative">
      {/* Matrix Rain Background */}
      <MatrixRain />
      
      {/* Scanlines Overlay */}
      <div className="scanlines fixed inset-0 pointer-events-none z-10" />

      {/* Boot Sequence Overlay */}
      {!bootComplete && (
        <motion.div
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={{ duration: 0.5, delay: 1 }}
          className="fixed inset-0 bg-[hsl(var(--cyber-dark))] z-50 flex items-center justify-center"
          data-testid="boot-sequence"
        >
          <div className="text-center space-y-4">
            <Terminal className="w-16 h-16 text-[hsl(var(--neon-green))] mx-auto animate-pulse-slow" />
            <p className="font-mono text-[hsl(var(--neon-green))] text-sm">
              INITIALIZING SYSTEM...<span className="terminal-cursor"></span>
            </p>
          </div>
        </motion.div>
      )}

      {/* Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 100 }}
        className="sticky top-0 z-40 bg-[hsl(var(--cyber-darker))]/95 backdrop-blur-sm border-b border-[hsl(var(--border))]"
        data-testid="header"
      >
        <div className="mx-auto px-4 sm:px-6 lg:px-10 max-w-6xl">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <motion.div
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.5 }}
                className="w-10 h-10 bg-[hsl(var(--neon-green))] border border-[hsl(var(--neon-green))] flex items-center justify-center neon-border"
                data-testid="logo"
              >
                <Terminal className="w-6 h-6 text-[hsl(var(--cyber-dark))]" />
              </motion.div>
              <span className="font-code text-lg font-bold text-[hsl(var(--neon-green))] neon-glow" data-testid="logo-text">
                root@system
              </span>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-6" data-testid="desktop-nav">
              <button
                onClick={() => scrollToSection('about')}
                className="font-mono text-sm text-[hsl(var(--terminal-green))] hover:text-[hsl(var(--neon-green))] hover-glow transition-all"
                data-testid="nav-about"
              >
                ./about
              </button>
              <button
                onClick={() => scrollToSection('skills')}
                className="font-mono text-sm text-[hsl(var(--terminal-green))] hover:text-[hsl(var(--neon-green))] hover-glow transition-all"
                data-testid="nav-skills"
              >
                ./skills
              </button>
              <button
                onClick={() => scrollToSection('projects')}
                className="font-mono text-sm text-[hsl(var(--terminal-green))] hover:text-[hsl(var(--neon-green))] hover-glow transition-all"
                data-testid="nav-projects"
              >
                ./projects
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="font-mono text-sm text-[hsl(var(--terminal-green))] hover:text-[hsl(var(--neon-green))] hover-glow transition-all"
                data-testid="nav-contact"
              >
                ./contact
              </button>
            </nav>

            {/* Access Level Counter */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              onClick={handleAccessGain}
              className="flex items-center gap-2 px-4 py-2 bg-[hsl(var(--cyber-darker))] border border-[hsl(var(--neon-green))] cursor-pointer hover:border-[hsl(var(--neon-cyan))] transition-all neon-border"
              data-testid="access-level-counter"
            >
              <Shield className="w-5 h-5 text-[hsl(var(--neon-green))]" />
              <span className="font-mono text-sm text-[hsl(var(--neon-green))]">LVL {accessLevel}</span>
            </motion.div>
          </div>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section
        id="about"
        className="relative py-12 sm:py-16 lg:py-24 overflow-hidden z-20"
        data-testid="hero-section"
      >
        <div className="absolute inset-0 cyber-grid opacity-30" />
        <div className="relative mx-auto px-4 sm:px-6 lg:px-10 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Terminal Avatar */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", duration: 0.8 }}
              className="flex justify-center"
              data-testid="hero-avatar"
            >
              <div className="relative">
                <div className="w-64 h-64 bg-[hsl(var(--cyber-darker))] border-2 border-[hsl(var(--neon-green))] neon-border flex items-center justify-center relative overflow-hidden">
                  <Terminal className="w-32 h-32 text-[hsl(var(--neon-green))] z-10 animate-pulse-slow" />
                  <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--neon-green))]/10 to-transparent" />
                </div>
                <motion.div
                  animate={{ 
                    scale: [1, 1.2, 1],
                    rotate: [0, 180, 360]
                  }}
                  transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                  className="absolute -top-4 -right-4"
                >
                  <Shield className="w-12 h-12 text-[hsl(var(--neon-cyan))]" />
                </motion.div>
                <motion.div
                  animate={{ 
                    opacity: [0.5, 1, 0.5]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute -bottom-4 -left-4"
                >
                  <Lock className="w-12 h-12 text-[hsl(var(--neon-purple))]" />
                </motion.div>
              </div>
            </motion.div>

            {/* Hero Content */}
            <div className="space-y-6" data-testid="hero-content">
              {/* ASCII Banner */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="mb-6 hidden lg:block"
              >
                <ASCIIBanner />
              </motion.div>

              <motion.div
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <div className="font-mono text-sm text-[hsl(var(--neon-cyan))] mb-2">
                  $ whoami
                </div>
                <h1 className="font-code text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight text-[hsl(var(--neon-green))] neon-glow glitch" data-testid="hero-title">
                  InternetGangster
                </h1>
                <div className="font-mono text-xs text-[hsl(var(--neon-purple))] mt-2">
                  [CICADA:3301] // GCFA // GCIH
                </div>
              </motion.div>
              
              <motion.div
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <div className="font-mono text-sm text-[hsl(var(--neon-cyan))] mb-2">
                  $ cat role.txt
                </div>
                <h2 className="font-mono text-base sm:text-lg text-[hsl(var(--neon-cyan))] tracking-wide" data-testid="hero-subtitle">
                  &gt; Security Researcher | Threat Hunter | AI Developer | Digital Forensics Expert
                </h2>
              </motion.div>

              <motion.div
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <div className="font-mono text-sm text-[hsl(var(--neon-cyan))] mb-2">
                  $ cat bio.txt
                </div>
                <p className="text-base leading-relaxed text-[hsl(var(--terminal-green))] font-mono" data-testid="hero-description">
                  Advanced incident responder specializing in memory forensics, malware analysis, and 
                  exploit development. Building AI systems for autonomous threat detection. Expert in 
                  offensive security, threat intelligence, and penetration testing.
                </p>
                <p className="text-base leading-relaxed text-[hsl(var(--terminal-green))] font-mono mt-2">
                  <span className="text-[hsl(var(--neon-green))]">Access level: 3301</span>
                </p>
              </motion.div>

              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="flex flex-wrap gap-4"
                data-testid="hero-cta-container"
              >
                <Button
                  onClick={() => scrollToSection('projects')}
                  className="cyber-button border border-[hsl(var(--neon-green))] bg-[hsl(var(--neon-green))]/10 text-[hsl(var(--neon-green))] hover:bg-[hsl(var(--neon-green))]/20 font-mono text-sm px-6 py-6"
                  data-testid="hero-primary-cta"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  ./view_projects
                </Button>
                <Button
                  onClick={() => scrollToSection('contact')}
                  variant="outline"
                  className="cyber-button border border-[hsl(var(--neon-cyan))] bg-[hsl(var(--neon-cyan))]/10 text-[hsl(var(--neon-cyan))] hover:bg-[hsl(var(--neon-cyan))]/20 font-mono text-sm px-6 py-6"
                  data-testid="hero-secondary-cta"
                >
                  <Send className="w-4 h-4 mr-2" />
                  ./contact
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section
        id="skills"
        className="py-12 sm:py-16 lg:py-24 bg-[hsl(var(--cyber-darker))] relative z-20"
        data-testid="skills-section"
      >
        <div className="mx-auto px-4 sm:px-6 lg:px-10 max-w-6xl">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="font-mono text-sm text-[hsl(var(--neon-cyan))] mb-2">
              $ cat /proc/capabilities
            </div>
            <h2 className="font-code text-4xl sm:text-5xl font-bold mb-4 text-[hsl(var(--neon-green))] neon-glow" data-testid="skills-title">
              System Capabilities
            </h2>
            <p className="text-base text-[hsl(var(--terminal-green))] font-mono mb-8" data-testid="skills-subtitle">
              &gt; Real-time system metrics and threat analysis
            </p>
            
            {/* System Status */}
            <SystemStatus />
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12" data-testid="skills-grid">
            {skills.map((skill, index) => (
              <motion.div
                key={skill.name}
                initial={{ scale: 0, rotate: -10 }}
                whileInView={{ scale: 1, rotate: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`skill-item-${index}`}
              >
                <Card className="cyber-card bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-6 h-full">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[hsl(var(--neon-cyan))]">
                      {skill.icon}
                    </div>
                    <span className="font-mono text-sm text-[hsl(var(--neon-green))]" data-testid={`skill-level-${index}`}>
                      {skill.level}%
                    </span>
                  </div>
                  <h3 className="font-mono text-base text-[hsl(var(--neon-green))] mb-2" data-testid={`skill-name-${index}`}>
                    {skill.name}
                  </h3>
                  <p className="font-mono text-xs text-[hsl(var(--muted-foreground))] mb-3">
                    {skill.desc}
                  </p>
                  <div
                    className="h-2 border border-[hsl(var(--border))] bg-[hsl(var(--muted))] overflow-hidden relative"
                    data-testid={`skill-stat-bar-${index}`}
                  >
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${skill.level}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, ease: [0.4, 0, 0.2, 1] }}
                      className="h-full relative"
                      style={{ backgroundColor: `hsl(var(${skill.color}))` }}
                    >
                      <div className="absolute inset-0 animate-pulse-slow" 
                           style={{ 
                             background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)` 
                           }} 
                      />
                    </motion.div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Kali Terminal Section */}
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="mt-16"
          >
            <div className="text-center mb-8">
              <div className="font-mono text-sm text-[hsl(var(--neon-cyan))] mb-2">
                $ sudo su -
              </div>
              <h3 className="font-code text-3xl font-bold text-[hsl(var(--neon-green))] neon-glow">
                Live Kali Linux Terminal
              </h3>
              <p className="text-sm text-[hsl(var(--terminal-green))] font-mono mt-2">
                &gt; Real-time penetration testing operations
              </p>
            </div>
            <KaliTerminal />
          </motion.div>
        </div>
      </section>

      {/* Projects Section */}
      <section
        id="projects"
        className="py-12 sm:py-16 lg:py-24 bg-[hsl(var(--background))] relative z-20"
        data-testid="projects-section"
      >
        <div className="absolute inset-0 cyber-grid opacity-20" />
        <div className="mx-auto px-4 sm:px-6 lg:px-10 max-w-6xl relative">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="font-mono text-sm text-[hsl(var(--neon-cyan))] mb-2">
              $ cat /var/log/projects.log
            </div>
            <h2 className="font-code text-4xl sm:text-5xl font-bold mb-4 text-[hsl(var(--neon-green))] neon-glow" data-testid="projects-title">
              Project Archive
            </h2>
            <p className="text-base text-[hsl(var(--terminal-green))] font-mono" data-testid="projects-subtitle">
              &gt; Classified operations and exploits
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ scale: 0, rotate: -10 }}
                whileInView={{ scale: 1, rotate: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -4 }}
                data-testid={`project-card-${index}`}
              >
                <Card
                  className="cyber-card bg-[hsl(var(--card))] border border-[hsl(var(--border))] cursor-pointer h-full relative overflow-hidden"
                  onClick={() => setSelectedProject(project)}
                >
                  {/* Classified Banner */}
                  <div className="absolute top-0 right-0 bg-[hsl(var(--neon-red))] text-black font-mono text-xs px-3 py-1 transform rotate-45 translate-x-8 translate-y-2">
                    CLASSIFIED
                  </div>
                  
                  <div className="p-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <h3 className="font-mono text-lg text-[hsl(var(--neon-green))] glitch" data-testid={`project-title-${index}`}>
                        {project.title}
                      </h3>
                      <div className="flex items-center gap-1 px-2 py-1 bg-[hsl(var(--neon-red))]/20 border border-[hsl(var(--neon-red))]">
                        <Lock className="w-3 h-3 text-[hsl(var(--neon-red))]" />
                        <span className="font-mono text-xs text-[hsl(var(--neon-red))]" data-testid={`project-access-${index}`}>
                          L{project.accessLevel}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-[hsl(var(--terminal-green))] font-mono" data-testid={`project-description-${index}`}>
                      {project.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {project.tags.map((tag, tagIndex) => (
                        <Badge
                          key={tag}
                          className="border border-[hsl(var(--border))] bg-[hsl(var(--muted))] text-[hsl(var(--neon-cyan))] hover:bg-[hsl(var(--neon-cyan))]/20 hover:border-[hsl(var(--neon-cyan))] font-mono text-xs px-2 py-1"
                          data-testid={`project-tag-${index}-${tagIndex}`}
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    <div className="pt-2 border-t border-[hsl(var(--border))]">
                      <p className="font-mono text-xs text-[hsl(var(--neon-red))] animate-flicker">
                        ⚠ AUTHORIZATION REQUIRED
                      </p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact"
        className="py-12 sm:py-16 lg:py-24 bg-[hsl(var(--cyber-darker))] relative z-20"
        data-testid="contact-section"
      >
        <div className="mx-auto px-4 sm:px-6 lg:px-10 max-w-3xl">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="font-mono text-sm text-[hsl(var(--neon-cyan))] mb-2">
              $ ./initiate_contact.sh
            </div>
            <h2 className="font-code text-4xl sm:text-5xl font-bold mb-4 text-[hsl(var(--neon-green))] neon-glow" data-testid="contact-title">
              Establish Connection
            </h2>
            <p className="text-base text-[hsl(var(--terminal-green))] font-mono" data-testid="contact-subtitle">
              &gt; Secure channel available for collaboration
            </p>
          </motion.div>

          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
          >
            <Card className="cyber-card bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-8">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label className="font-mono text-sm block mb-2 text-[hsl(var(--neon-cyan))]" htmlFor="name">
                    $ echo "name"
                  </label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter your identifier..."
                    className="border border-[hsl(var(--border))] bg-[hsl(var(--input))] text-[hsl(var(--terminal-green))] focus:ring-2 focus:ring-[hsl(var(--neon-green))] font-mono placeholder:text-[hsl(var(--muted-foreground))]"
                    required
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="font-mono text-sm block mb-2 text-[hsl(var(--neon-cyan))]" htmlFor="email">
                    $ echo "email"
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="user@domain.tld"
                    className="border border-[hsl(var(--border))] bg-[hsl(var(--input))] text-[hsl(var(--terminal-green))] focus:ring-2 focus:ring-[hsl(var(--neon-green))] font-mono placeholder:text-[hsl(var(--muted-foreground))]"
                    required
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="font-mono text-sm block mb-2 text-[hsl(var(--neon-cyan))]" htmlFor="message">
                    $ cat message.txt
                  </label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Transmit your encrypted message..."
                    className="border border-[hsl(var(--border))] bg-[hsl(var(--input))] text-[hsl(var(--terminal-green))] focus:ring-2 focus:ring-[hsl(var(--neon-green))] min-h-[120px] font-mono placeholder:text-[hsl(var(--muted-foreground))]"
                    required
                    data-testid="contact-message-textarea"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full cyber-button border border-[hsl(var(--neon-green))] bg-[hsl(var(--neon-green))]/10 text-[hsl(var(--neon-green))] hover:bg-[hsl(var(--neon-green))]/20 font-mono text-sm py-6"
                  data-testid="contact-submit-button"
                >
                  <Send className="w-4 h-4 mr-2" />
                  ./send_encrypted
                </Button>
              </form>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="bg-[hsl(var(--cyber-dark))] border-t border-[hsl(var(--border))] text-[hsl(var(--terminal-green))] py-12 relative z-20"
        data-testid="footer"
      >
        <div className="mx-auto px-4 sm:px-6 lg:px-10 max-w-6xl">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* About */}
            <div data-testid="footer-about">
              <h3 className="font-code text-xl mb-4 text-[hsl(var(--neon-green))] neon-glow">InternetGangster</h3>
              <p className="text-sm text-[hsl(var(--terminal-green))] font-mono">
                Security researcher and threat hunter. Penetrating systems and building intelligent defense mechanisms. 
                <span className="text-[hsl(var(--neon-cyan))]"> Always online.</span>
              </p>
            </div>

            {/* Quick Links */}
            <div data-testid="footer-links">
              <h3 className="font-code text-xl mb-4 text-[hsl(var(--neon-green))]">Navigation</h3>
              <div className="space-y-2">
                <button
                  onClick={() => scrollToSection('about')}
                  className="block text-sm text-[hsl(var(--terminal-green))] hover:text-[hsl(var(--neon-cyan))] transition-colors font-mono"
                  data-testid="footer-link-about"
                >
                  $ ./about
                </button>
                <button
                  onClick={() => scrollToSection('skills')}
                  className="block text-sm text-[hsl(var(--terminal-green))] hover:text-[hsl(var(--neon-cyan))] transition-colors font-mono"
                  data-testid="footer-link-skills"
                >
                  $ ./skills
                </button>
                <button
                  onClick={() => scrollToSection('projects')}
                  className="block text-sm text-[hsl(var(--terminal-green))] hover:text-[hsl(var(--neon-cyan))] transition-colors font-mono"
                  data-testid="footer-link-projects"
                >
                  $ ./projects
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="block text-sm text-[hsl(var(--terminal-green))] hover:text-[hsl(var(--neon-cyan))] transition-colors font-mono"
                  data-testid="footer-link-contact"
                >
                  $ ./contact
                </button>
              </div>
            </div>

            {/* Social */}
            <div data-testid="footer-social">
              <h3 className="font-code text-xl mb-4 text-[hsl(var(--neon-green))]">Connect</h3>
              <div className="flex gap-4">
                <motion.a
                  whileHover={{ y: -4, scale: 1.1 }}
                  href="https://twitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-[hsl(var(--cyber-darker))] border border-[hsl(var(--border))] flex items-center justify-center hover:border-[hsl(var(--neon-cyan))] hover:bg-[hsl(var(--neon-cyan))]/10 transition-all"
                  data-testid="footer-social-twitter"
                >
                  <Twitter className="w-6 h-6 text-[hsl(var(--neon-cyan))]" />
                </motion.a>
                <motion.a
                  whileHover={{ y: -4, scale: 1.1 }}
                  href="https://t.me"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-[hsl(var(--cyber-darker))] border border-[hsl(var(--border))] flex items-center justify-center hover:border-[hsl(var(--neon-green))] hover:bg-[hsl(var(--neon-green))]/10 transition-all"
                  data-testid="footer-social-telegram"
                >
                  <Send className="w-6 h-6 text-[hsl(var(--neon-green))]" />
                </motion.a>
                <motion.a
                  whileHover={{ y: -4, scale: 1.1 }}
                  href="https://mastodon.social"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-[hsl(var(--cyber-darker))] border border-[hsl(var(--border))] flex items-center justify-center hover:border-[hsl(var(--neon-purple))] hover:bg-[hsl(var(--neon-purple))]/10 transition-all"
                  data-testid="footer-social-mastodon"
                >
                  <MessageSquare className="w-6 h-6 text-[hsl(var(--neon-purple))]" />
                </motion.a>
                <motion.a
                  whileHover={{ y: -4, scale: 1.1 }}
                  href="mailto:contact@example.com"
                  className="w-12 h-12 bg-[hsl(var(--cyber-darker))] border border-[hsl(var(--border))] flex items-center justify-center hover:border-[hsl(var(--neon-red))] hover:bg-[hsl(var(--neon-red))]/10 transition-all"
                  data-testid="footer-social-email"
                >
                  <Mail className="w-6 h-6 text-[hsl(var(--neon-red))]" />
                </motion.a>
              </div>
            </div>
          </div>

          <div className="border-t border-[hsl(var(--border))] pt-8 text-center">
            <p className="font-mono text-xs text-[hsl(var(--muted-foreground))]" data-testid="footer-copyright">
              © 2025 InternetGangster | <span className="text-[hsl(var(--neon-green))]">root@system:~#</span> All systems operational
            </p>
          </div>
        </div>
      </footer>

      {/* Project Dialog */}
      <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
        <DialogContent className="cyber-card bg-[hsl(var(--card))] border-2 border-[hsl(var(--neon-green))] max-w-2xl" data-testid="project-dialog">
          {selectedProject && (
            <>
              <DialogHeader>
                <DialogTitle className="font-code text-2xl text-[hsl(var(--neon-green))] neon-glow glitch" data-testid="project-dialog-title">
                  {selectedProject.title}
                </DialogTitle>
                <DialogDescription className="text-base text-[hsl(var(--terminal-green))] font-mono" data-testid="project-dialog-description">
                  {selectedProject.details}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {selectedProject.tags.map((tag) => (
                    <Badge
                      key={tag}
                      className="border border-[hsl(var(--border))] bg-[hsl(var(--neon-green))]/10 text-[hsl(var(--neon-green))] font-mono text-xs px-3 py-1"
                      data-testid="project-dialog-tag"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 px-3 py-2 bg-[hsl(var(--neon-red))]/20 border border-[hsl(var(--neon-red))]">
                    <Lock className="w-4 h-4 text-[hsl(var(--neon-red))]" />
                    <span className="font-mono text-sm text-[hsl(var(--neon-red))]">
                      CLEARANCE LEVEL {selectedProject.accessLevel} REQUIRED
                    </span>
                  </div>
                </div>
                <div className="p-4 bg-[hsl(var(--cyber-darker))] border border-[hsl(var(--border))]">
                  <p className="font-mono text-xs text-[hsl(var(--neon-cyan))] mb-2">$ cat access_log.txt</p>
                  <p className="font-mono text-xs text-[hsl(var(--terminal-green))]">
                    [SYSTEM] Access attempt logged<br/>
                    [SYSTEM] Current clearance: Level {accessLevel}<br/>
                    [SYSTEM] Required clearance: Level {selectedProject.accessLevel}<br/>
                    {accessLevel >= selectedProject.accessLevel ? (
                      <span className="text-[hsl(var(--neon-green))]">[SUCCESS] Access granted ✓</span>
                    ) : (
                      <span className="text-[hsl(var(--neon-red))]">[DENIED] Insufficient privileges ✗</span>
                    )}
                  </p>
                </div>
                <div className="flex gap-4">
                  <Button
                    disabled={accessLevel < selectedProject.accessLevel}
                    className="cyber-button border border-[hsl(var(--neon-green))] bg-[hsl(var(--neon-green))]/10 text-[hsl(var(--neon-green))] hover:bg-[hsl(var(--neon-green))]/20 font-mono text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="project-dialog-access-btn"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    ./request_access
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setSelectedProject(null)}
                    className="cyber-button border border-[hsl(var(--border))] bg-transparent text-[hsl(var(--terminal-green))] hover:bg-[hsl(var(--muted))] font-mono text-sm"
                    data-testid="project-dialog-close-btn"
                  >
                    ./exit
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default App;
